import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface BurgerMenuProps {
  currentPage?: 'lists' | 'listDetail' | 'settings' | 'admin' | 'supermarkets';
  onShowMembers?: () => void;
  onShare?: () => void;
  membersCount?: number;
}

export default function BurgerMenu({ currentPage, onShowMembers, onShare, membersCount }: BurgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const closeMenu = () => setIsOpen(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMenu();
  };

  const handleLogout = () => {
    logout();
    closeMenu();
  };

  return (
    <>
      {/* Burger Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="burger-menu-button"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          zIndex: 1001,
        }}
        aria-label="Menü"
      >
        <span style={{
          width: '24px',
          height: '2px',
          backgroundColor: 'var(--gray-700)',
          transition: 'all 0.3s ease',
          transform: isOpen ? 'rotate(45deg) translateY(6px)' : 'none',
        }} />
        <span style={{
          width: '24px',
          height: '2px',
          backgroundColor: 'var(--gray-700)',
          transition: 'all 0.3s ease',
          opacity: isOpen ? 0 : 1,
        }} />
        <span style={{
          width: '24px',
          height: '2px',
          backgroundColor: 'var(--gray-700)',
          transition: 'all 0.3s ease',
          transform: isOpen ? 'rotate(-45deg) translateY(-6px)' : 'none',
        }} />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={closeMenu}
        />
      )}

      {/* Menu */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-280px',
          width: '280px',
          height: '100vh',
          backgroundColor: 'var(--bg-primary)',
          boxShadow: 'var(--shadow-xl)',
          transition: 'right 0.3s ease',
          zIndex: 1000,
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Menu Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Angemeldet als
          </div>
          <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
            {user?.name}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {user?.email}
          </div>
        </div>

        {/* Menu Items */}
        <nav style={{ flex: 1, padding: '1rem' }}>
          {currentPage !== 'lists' && (
            <button
              onClick={() => handleNavigation('/')}
              className="burger-menu-item"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--gray-700)',
                borderRadius: '0.5rem',
                transition: 'background-color 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span>📋</span>
              <span>Meine Listen</span>
            </button>
          )}

          {currentPage === 'listDetail' && onShowMembers && membersCount && membersCount > 1 && (
            <button
              onClick={() => {
                onShowMembers();
                closeMenu();
              }}
              className="burger-menu-item"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--gray-700)',
                borderRadius: '0.5rem',
                transition: 'background-color 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span>👥</span>
              <span>Mitglieder ({membersCount})</span>
            </button>
          )}

          {currentPage === 'listDetail' && onShare && (
            <button
              onClick={() => {
                onShare();
                closeMenu();
              }}
              className="burger-menu-item"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--gray-700)',
                borderRadius: '0.5rem',
                transition: 'background-color 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span>➕</span>
              <span>Einladen</span>
            </button>
          )}

          {/* Supermarkets */}
          <button
            onClick={() => handleNavigation('/supermarkets')}
            className="burger-menu-item"
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: 'var(--gray-700)',
              borderRadius: '0.5rem',
              transition: 'background-color 0.2s',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>🏪</span>
            <span>Meine Supermärkte</span>
          </button>

          {currentPage !== 'settings' && (
            <button
              onClick={() => handleNavigation('/settings')}
              className="burger-menu-item"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--gray-700)',
                borderRadius: '0.5rem',
                transition: 'background-color 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span>⚙️</span>
              <span>Einstellungen</span>
            </button>
          )}

          {user?.isAdmin && (
            <button
              onClick={() => handleNavigation('/admin')}
              className="burger-menu-item"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--primary)',
                borderRadius: '0.5rem',
                transition: 'background-color 0.2s',
                textAlign: 'left',
                fontWeight: '600',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span>🔧</span>
              <span>Admin</span>
            </button>
          )}

          <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '1rem 0' }} />

          <button
            onClick={() => {
              toggleDarkMode();
            }}
            className="burger-menu-item"
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: 'var(--text-primary)',
              borderRadius: '0.5rem',
              transition: 'background-color 0.2s',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--gray-100)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>{isDarkMode ? '☀️' : '🌙'}</span>
            <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '1rem 0' }} />

          <button
            onClick={handleLogout}
            className="burger-menu-item"
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: 'var(--danger)',
              borderRadius: '0.5rem',
              transition: 'background-color 0.2s',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>🚪</span>
            <span>Abmelden</span>
          </button>
        </nav>

        {/* Footer with Version */}
        <div style={{
          padding: '1rem',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          <button
            onClick={() => handleNavigation('/changelog')}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontSize: '0.75rem',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
          >
            Version 1.1.0
          </button>
        </div>
      </div>
    </>
  );
}
